import { FC, forwardRef, useMemo } from 'react'
import { ReactCalendarGroupRendererProps } from 'react-calendar-timeline'
import { useTranslation } from 'react-i18next'

import { Grid, Link, Typography } from '@mui/material'

import { GRTooltip } from '../../../../../../components/GRTooltip/GRTooltip'
import languageService from '../../../../../../services/LanguageService'
import { useCurrentUserLanguage } from '../../../../../account/hooks/useCurrentUserLanguage'
import { LiveEventTagGroup } from '../../../../types/LiveEventTagGroup'
import { TrackingEventTimelineGroup } from '../../../../types/LiveEvents'
import { TrackedGame } from '../../../../types/TrackedGame'
import { EventTagGroupIndicator } from '../../../EventTagGroupIndicator/EventTagGroupIndicator'
import './LiveEventsCalendarTrackingEventGroup.scss'

type LiveEventsCalendarTrackingEventGroupProps = ReactCalendarGroupRendererProps<TrackingEventTimelineGroup> & {
  onClick: (group: TrackingEventTimelineGroup) => void
  onBlueprintClick?: (group: TrackingEventTimelineGroup) => void
  groupByBlueprint: boolean
}

export const LiveEventsCalendarTrackingEventGroup: FC<LiveEventsCalendarTrackingEventGroupProps> = forwardRef<
  HTMLDivElement,
  LiveEventsCalendarTrackingEventGroupProps
>(({ group, onClick, onBlueprintClick, groupByBlueprint }, ref) => {
  const { t } = useTranslation()
  const userLanguage = useCurrentUserLanguage()
  const groupByBlueprintResolvedTitles = useMemo(() => {
    let blueprintName = ''
    let eventTypeName = ''

    if (groupByBlueprint) {
      eventTypeName = languageService.getTranslation('tags', group.eventTypeId)
      if (group.blueprintId) {
        const blueprint = (group.trackedGame as TrackedGame).blueprints.find((blueprint) => group.blueprintId === blueprint.blueprintId)
        blueprintName = blueprint?.names[userLanguage] || blueprint?.names['en'] || '?'
      } else {
        blueprintName = '-'
      }
    }

    return {
      blueprintName: blueprintName,
      eventTypeName: eventTypeName,
    }
  }, [groupByBlueprint, group, userLanguage])

  return (
    <Grid
      container
      alignItems="center"
      justifyContent="space-between"
      ref={ref}
      style={{ position: 'relative' }}
      id={(group.trackedGame.game.id + '-' + group.id) as string}
      className="LiveEventsCalendarTrackingEventGroup"
      sx={{ width: '100%', minWidth: 0, maxWidth: '100%' }}
    >
      <EventTagGroupIndicator colorHex={group.colorHex} sx={{ left: '-8px' }} />

      {groupByBlueprint && (
        <Grid container direction="row" alignItems="stretch" sx={{ height: '100%', minWidth: 0, flexShrink: 1, flexGrow: 1, width: '100%', maxWidth: '100%' }}>
          <Grid
            item
            xs={6}
            paddingRight={2}
            className="LiveEventsCalendarTrackingEventGroup__EventTypeName"
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', height: '100%', minWidth: 0, flexShrink: 1, maxWidth: '50%' }}
          >
            {group.tagGroupId === LiveEventTagGroup.DEFINING_OCCURRENCES ? (
              <GRTooltip content={groupByBlueprintResolvedTitles.eventTypeName}>
                <Typography sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%', minWidth: 0, fontWeight: 'bold' }}>
                  {groupByBlueprintResolvedTitles.eventTypeName}
                </Typography>
              </GRTooltip>
            ) : (
              <GRTooltip content={groupByBlueprintResolvedTitles.eventTypeName}>
                <Link
                  underline="none"
                  sx={{
                    color: (theme) => theme.palette.text.primary,
                    '&:hover': { color: (theme) => theme.palette.primary.main, cursor: 'pointer' },
                    display: 'block',
                    alignItems: 'center',
                    height: '100%',
                    width: '100%',
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  onClick={() => onClick(group as TrackingEventTimelineGroup)}
                >
                  {groupByBlueprintResolvedTitles.eventTypeName}
                </Link>
              </GRTooltip>
            )}
          </Grid>
          <Grid
            item
            xs={6}
            textAlign="right"
            paddingLeft={0.5}
            className="LiveEventsCalendarTrackingEventGroup__BlueprintName"
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', height: '100%', minWidth: 0, flexShrink: 1, maxWidth: '50%' }}
          >
            <GRTooltip content={group.blueprintId ? groupByBlueprintResolvedTitles.blueprintName : t('live-events:no_blueprint')}>
              {group.blueprintId ? (
                <Link
                  underline="none"
                  sx={{
                    color: (theme) => theme.palette.text.primary,
                    '&:hover': { color: (theme) => theme.palette.primary.main, cursor: 'pointer' },
                    display: 'block',
                    width: '100%',
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  onClick={() => onBlueprintClick?.(group as TrackingEventTimelineGroup)}
                >
                  <Typography fontSize={12} sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                    {groupByBlueprintResolvedTitles.blueprintName}
                  </Typography>
                </Link>
              ) : (
                <Typography fontSize={12} sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%', minWidth: 0 }}>
                  {groupByBlueprintResolvedTitles.blueprintName}
                </Typography>
              )}
            </GRTooltip>
          </Grid>
        </Grid>
      )}

      {!groupByBlueprint && group.tagGroupId !== LiveEventTagGroup.DEFINING_OCCURRENCES && (
        <Link
          underline="none"
          sx={{
            color: (theme) => theme.palette.text.primary,
            '&:hover': { color: (theme) => theme.palette.primary.main, cursor: 'pointer' },
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '100%',
            minWidth: 0,
            display: 'block',
          }}
          onClick={() => onClick(group as TrackingEventTimelineGroup)}
        >
          {group.title}
        </Link>
      )}

      {!groupByBlueprint && group.tagGroupId === LiveEventTagGroup.DEFINING_OCCURRENCES && (
        <Typography sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%', minWidth: 0, fontWeight: 'bold' }}>
          {group.title}
        </Typography>
      )}
    </Grid>
  )
})
